import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, ArrowLeft, Search, AlertTriangle } from 'lucide-react';

const NotFound: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-16 h-16 bg-red-500 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-6xl font-bold text-textBase">404</h1>
              <p className="text-2xl text-grey-600">Page Not Found</p>
            </div>
          </div>
          
          <p className="text-xl text-grey-600 max-w-2xl mx-auto">
            Oops! The page you're looking for doesn't exist. It might have been moved, deleted, or you entered the wrong URL.
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm p-8"
        >
          {/* Navigation Options */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
                <Home className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-textBase mb-3">Go Home</h3>
              <p className="text-grey-600 mb-4">
                Return to the main page and explore our Financial Goal Tracker features.
              </p>
              <Link
                to="/"
                className="inline-flex items-center btn-primary"
              >
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-textBase mb-3">Popular Pages</h3>
              <p className="text-grey-600 mb-4">
                Check out these commonly visited pages on our site.
              </p>
              <div className="space-y-2">
                <Link
                  to="/privacy"
                  className="block text-primary hover:text-primary-dark transition-colors duration-200"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="block text-primary hover:text-primary-dark transition-colors duration-200"
                >
                  Terms & Conditions
                </Link>
                <Link
                  to="/delete-account"
                  className="block text-primary hover:text-primary-dark transition-colors duration-200"
                >
                  Delete Account
                </Link>
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-textBase mb-3">Need Help?</h3>
            <p className="text-grey-600 mb-4">
              If you're having trouble finding what you're looking for, here are some things you can try:
            </p>
            <ul className="space-y-2 text-grey-600">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Check the URL for typos or spelling errors</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Use the navigation menu to browse our site</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Contact our support team if you need assistance</span>
              </li>
            </ul>
          </div>

          {/* Contact Information */}
          <div className="mt-8 text-center">
            <p className="text-grey-600 mb-4">
              Still can't find what you're looking for?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="mailto:support@fininteract.com"
                className="inline-flex items-center text-primary hover:text-primary-dark transition-colors duration-200"
              >
                <span>Contact Support</span>
              </a>
              <span className="text-grey-400">•</span>
              <span className="text-grey-600">support@fininteract.com</span>
            </div>
          </div>
        </motion.div>

        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-8"
        >
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center text-grey-600 hover:text-textBase transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound;
